import React, { useState, useEffect } from 'react';
import { GameDetailStatsResponse, CurrencyStat, GameStatisticGroupedByTimeResponse, CurrencyInfo } from '../types';
import { apiService } from '../api';
import LoadingSpinner from './LoadingSpinner';
import { BarChart, ChartDataPoint } from './charts';

interface GameDetailStatsPageProps {
  gameId: number;
  gameName: string;
  onBack: () => void;
  isAdmin?: boolean;
  onShowUserStats?: (gameId: number, gameName: string) => void;
}

const GameDetailStatsPage: React.FC<GameDetailStatsPageProps> = ({ gameId, gameName, onBack, isAdmin, onShowUserStats }) => {
  const [stats, setStats] = useState<GameDetailStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Get user's timezone offset (in hours, from -12 to +12)
  const getUserTimezoneOffset = (): number => {
    const offsetMinutes = new Date().getTimezoneOffset();
    // getTimezoneOffset returns offset in minutes, negative for positive timezones
    // Convert to hours and invert sign
    const offsetHours = -offsetMinutes / 60;
    // Clamp to valid range [-12, 12]
    return Math.max(-12, Math.min(12, Math.round(offsetHours)));
  };

  const [timezoneOffset, setTimezoneOffset] = useState<number>(getUserTimezoneOffset());
  const [groupedStats, setGroupedStats] = useState<GameStatisticGroupedByTimeResponse | null>(null);
  const [groupBy, setGroupBy] = useState<'day' | 'hour'>('day');
  const [loadingGrouped, setLoadingGrouped] = useState<boolean>(false);
  const [chartCurrency, setChartCurrency] = useState<string>('');
  const [currencies, setCurrencies] = useState<CurrencyInfo[]>([]);

  const loadGroupedStats = async () => {
    if (!startDate || !endDate) {
      setGroupedStats(null);
      return;
    }

    try {
      setLoadingGrouped(true);
      const startUTC = new Date(startDate + 'T00:00:00Z');
      const startTimestamp = startUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
      
      const endUTC = new Date(endDate + 'T23:59:59.999Z');
      const endTimestamp = endUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);

      const response = await apiService.getGameStatsGrouped({
        gameId,
        startDate: startTimestamp,
        endDate: endTimestamp,
        groupBy,
        timezoneOffset,
        currency: chartCurrency || undefined,
      });

      if (response.success) {
        setGroupedStats(response);
      }
    } catch (err: any) {
      console.error('Grouped stats error:', err);
    } finally {
      setLoadingGrouped(false);
    }
  };

  const loadStats = async (ignorePeriod: boolean = false) => {
    try {
      setLoading(true);
      setError('');
      const request: any = { gameId };
      if (!ignorePeriod && startDate && endDate) {
        // Create date in UTC from the date string (YYYY-MM-DD)
        // The date string is interpreted as midnight UTC, then we apply the timezone offset
        const startUTC = new Date(startDate + 'T00:00:00Z');
        // Apply timezone offset: if user selected UTC+3, we need to subtract 3 hours from UTC
        // to get the start of day in that timezone (in UTC representation)
        request.startDate = startUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
        
        // Create end date in UTC
        const endUTC = new Date(endDate + 'T23:59:59.999Z');
        // Apply timezone offset
        request.endDate = endUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
      }
      
      const response = await apiService.getGameDetailStats(request);
      if (response.success) {
        setStats(response);
      } else {
        setError(response.error || 'Failed to load game statistics');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load game statistics');
      console.error('Game detail stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load initial stats on mount (without period)
  useEffect(() => {
    apiService.getCurrencies().then(res => {
      if (res.success) setCurrencies(res.currencies || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const loadInitialStats = async () => {
      try {
        setLoading(true);
        setError('');
        const request: any = { gameId };
        
        const response = await apiService.getGameDetailStats(request);
        if (response.success) {
          setStats(response);
        } else {
          setError(response.error || 'Failed to load game statistics');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load game statistics');
        console.error('Game detail stats error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadInitialStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  const handlePeriodSubmit = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999);

    if (end > now) {
      setError('End date cannot be in the future');
      return;
    }

    if (start > end) {
      setError('Start date must be before end date');
      return;
    }

    await Promise.all([loadStats(), loadGroupedStats()]);
  };

  useEffect(() => {
    if (startDate && endDate) {
      loadGroupedStats();
    } else {
      setGroupedStats(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  const handleResetPeriod = async () => {
    setStartDate('');
    setEndDate('');
    await loadStats(true); // Ignore period when resetting
  };

  const getMaxDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const isDatesValid = (): boolean => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return start <= end && end <= now;
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Generate timezone options from UTC-12 to UTC+12
  const timezoneOptions = Array.from({ length: 25 }, (_, i) => {
    const offset = i - 12; // From -12 to +12
    const sign = offset >= 0 ? '+' : '';
    return { value: offset, label: `UTC${sign}${offset}` };
  });

  if (loading && !stats) {
    return <LoadingSpinner />;
  }

  return (
    <div className="game-detail-stats-page" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, marginBottom: '5px' }}>{gameName} Statistics</h1>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Game ID: {gameId}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {isAdmin && onShowUserStats && (
            <button 
              onClick={() => onShowUserStats(gameId, gameName)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              👥 User Statistics
            </button>
          )}
          <button onClick={onBack} className="btn btn-logout" style={{ padding: '8px 16px' }}>
            ← Back
          </button>
        </div>
      </div>

      {/* Date range selector */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '15px', 
        backgroundColor: '#f9f9f9', 
        borderRadius: '8px',
        border: '1px solid #ddd'
      }}>
        <div style={{ marginBottom: '10px', fontWeight: '500', color: '#333' }}>
          Select Date Range (Optional)
        </div>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={getMaxDate()}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              max={getMaxDate()}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Timezone</label>
            <select
              value={timezoneOffset}
              onChange={(e) => setTimezoneOffset(Number(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {timezoneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {startDate && endDate && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Group By</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as 'day' | 'hour')}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '150px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="day">Day</option>
                <option value="hour">Hour</option>
              </select>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Currency (optional)</label>
            <select
              value={chartCurrency}
              onChange={(e) => setChartCurrency(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', minWidth: '120px', backgroundColor: 'white', cursor: 'pointer' }}
            >
              <option value="">All</option>
              {currencies.map((c) => (
                <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handlePeriodSubmit}
            disabled={!isDatesValid() || loading}
            style={{
              padding: '10px 24px',
              backgroundColor: isDatesValid() && !loading ? '#4CAF50' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isDatesValid() && !loading ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              marginTop: '20px',
              transition: 'all 0.2s ease'
            }}
          >
            Apply Period
          </button>
          {(startDate || endDate) && (
            <button
              onClick={handleResetPeriod}
              style={{
                padding: '10px 16px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginTop: '20px'
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ 
          padding: '12px', 
          backgroundColor: '#fee', 
          color: '#c33', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {stats && (
        <>
          {/* Overall Statistics */}
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ marginBottom: '15px', color: '#333' }}>Overall Statistics (All Currencies)</h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: startDate && endDate ? 'repeat(auto-fit, minmax(250px, 1fr))' : 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              {startDate && endDate && stats.periodVolumeUSD !== undefined && stats.periodGGRUSD !== undefined ? (
                // Period mode: show only Volume and GGR for the period
                <>
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Volume USD</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: stats.periodVolumeUSD < 0 ? '#d32f2f' : '#333' }}>
                      {formatCurrency(stats.periodVolumeUSD)}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>GGR USD</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: stats.periodGGRUSD < 0 ? '#d32f2f' : '#333' }}>
                      {formatCurrency(stats.periodGGRUSD)}
                    </div>
                  </div>
                </>
              ) : (
                // Default mode: show Hourly and Daily statistics
                <>
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Hourly Volume</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: (stats.hourlyVolumeUSD ?? 0) < 0 ? '#d32f2f' : '#333' }}>
                      {formatCurrency(stats.hourlyVolumeUSD ?? 0)}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Hourly GGR</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: (stats.hourlyGGRUSD ?? 0) < 0 ? '#d32f2f' : '#333' }}>
                      {formatCurrency(stats.hourlyGGRUSD ?? 0)}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Daily Volume</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: (stats.dailyVolumeUSD ?? 0) < 0 ? '#d32f2f' : '#333' }}>
                      {formatCurrency(stats.dailyVolumeUSD ?? 0)}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Daily GGR</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: (stats.dailyGGRUSD ?? 0) < 0 ? '#d32f2f' : '#333' }}>
                      {formatCurrency(stats.dailyGGRUSD ?? 0)}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Chart Section */}
          {startDate && endDate && groupedStats && groupedStats.data.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ marginBottom: '15px', color: '#333' }}>
                Statistics Chart ({groupBy === 'day' ? 'Daily' : 'Hourly'})
              </h2>
              <div style={{
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0'
              }}>
                {loadingGrouped ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingSpinner />
                  </div>
                ) : (
                  <GameStatsChart
                    data={groupedStats.data}
                    groupBy={groupBy}
                    timezoneOffset={timezoneOffset}
                  />
                )}
              </div>
            </div>
          )}

          {/* Currency Statistics */}
          <div>
            <h2 style={{ marginBottom: '15px', color: '#333' }}>
              {startDate && endDate ? 'Period Statistics by Currency' : 'Hourly Statistics by Currency'}
            </h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #ddd'
                    }}>
                      Currency
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      borderBottom: '2px solid #ddd'
                    }}>
                      Volume
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      borderBottom: '2px solid #ddd'
                    }}>
                      GGR
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.currencyStats.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        No currency statistics available
                      </td>
                    </tr>
                  ) : (
                    stats.currencyStats.map((stat: CurrencyStat) => (
                      <tr key={stat.currency} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: '500' }}>{stat.currency}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: stat.volume < 0 ? '#d32f2f' : 'inherit' }}>{formatNumber(stat.volume)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: stat.ggr < 0 ? '#d32f2f' : 'inherit' }}>{formatNumber(stat.ggr)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** Adapter: game stats grouped by time -> BarChart format */
function GameStatsChart({
  data,
  groupBy,
  timezoneOffset = 0,
}: {
  data: GameStatisticGroupedByTimeResponse['data'];
  groupBy: 'day' | 'hour';
  timezoneOffset?: number;
}) {
  const chartData: ChartDataPoint<number>[] = data.map(point => ({
    x: point.timestamp,
    series: Object.fromEntries(point.currencies.map(c => [c.currency, parseFloat(c.ggr)])),
  }));

  const tz = timezoneOffset !== 0 ? `Etc/GMT${timezoneOffset <= 0 ? '+' : '-'}${Math.abs(timezoneOffset)}` : undefined;
  const dateOptions: Intl.DateTimeFormatOptions = tz ? { timeZone: tz } : {};

  const formatXLabel = (ts: number) => {
    const d = new Date(ts);
    return groupBy === 'day'
      ? d.toLocaleDateString('en-US', { ...dateOptions, month: 'short', day: 'numeric' })
      : d.toLocaleString('en-US', { ...dateOptions, hour: '2-digit', minute: '2-digit' });
  };

  const formatXLabelFull = (ts: number) => {
    const d = new Date(ts);
    return groupBy === 'day'
      ? d.toLocaleDateString('en-US', { ...dateOptions, month: 'short', day: 'numeric' })
      : d.toLocaleString('en-US', { ...dateOptions, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatYValue = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

  const formatTooltip = (x: number, _s: string, v: number) =>
    `Дата: ${formatXLabelFull(x)} — GGR: ${formatYValue(v)}`;

  return (
    <BarChart<number>
      data={chartData}
      formatXLabel={formatXLabel}
      formatXLabelFull={formatXLabelFull}
      formatYValue={formatYValue}
      formatTooltip={formatTooltip}
      legendSuffix=" GGR"
      minBarGroupWidth={groupBy === 'hour' ? 30 : 50}
    />
  );
}

export default GameDetailStatsPage;
